import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TeacherLedgerService from '../../services/TeacherLedgerService';
import { buildSchoolYearRange } from '../common/schoolYear';
import { displayDate } from '../common/datePresentation';
import formatPayrollAmount, { hasPayrollAlerts, settlementMatchesSearch } from './teacherSettlementPresentation';
import { canReadPayments } from '../manage/rbac';
import buildSchoolYearMonthOptions from './clientSettlementsMonthOptions';
import { exportPayrollCsv, printPayroll } from './teacherPayrollExports';
import TeacherSettlementDetails from './TeacherSettlementDetails';
import TeacherSettlementStatus from './TeacherSettlementStatus';
import { groupSettlementLessons } from './teacherSettlementGroups';
import { settlementContextKey, settlementReturnState, settlementAttendanceLocation, isPlainNavigationClick } from './teacherSettlementNavigation';
import './teacherSettlements.scss';

export function TeacherSettlementsReport({ activeSchoolYearStartYear, user, personal, service, history, location }) {
  const { t: tr, i18n } = useTranslation();
  const t = (key, values) => tr(`billing:teacherPayroll.${key}`, values);
  const language = i18n.resolvedLanguage || i18n.language || 'pl';
  const formatLedgerAmount = value => formatPayrollAmount(value, language);
  const errorText = value => {
    let code = value;
    try { code = JSON.parse(value).error || value; } catch (_) { /* Plain text errors are already codes. */ }
    return tr(`billing:teacherPayroll.errors.${code}`, { defaultValue: t('error') });
  };
  const options = useMemo(() => buildSchoolYearMonthOptions(buildSchoolYearRange(activeSchoolYearStartYear)), [activeSchoolYearStartYear]);
  const contextKey = settlementContextKey({ user, schoolYearStartYear: activeSchoolYearStartYear, personal });
  const savedView = (location.state || {}).teacherSettlementView;
  const restored = savedView && savedView.contextKey === contextKey ? savedView : null;
  const [chosenMonth, setChosenMonth] = useState(restored ? restored.month : '');
  const month = options.some(item => item.value === chosenMonth) ? chosenMonth : (options[0] || {}).value;
  const context = `${contextKey}:${month}`;
  const [viewState, setViewState] = useState({ context,
    expandedTeacherIds: restored ? restored.expandedTeacherIds : [],
    openedTeacherIds: restored ? restored.expandedTeacherIds : [],
    expandedGroupKeys: restored ? restored.expandedGroupKeys : [],
    missingOnly: restored ? restored.missingOnly : false,
    alertsOnly: restored ? Boolean(restored.alertsOnly) : false,
    withLessonsOnly: restored ? Boolean(restored.withLessonsOnly) : false,
    searchQuery: restored ? String(restored.searchQuery || '').slice(0, 200) : '' });
  const view = viewState.context === context ? viewState : { context,
    expandedTeacherIds: [],
    openedTeacherIds: [],
    expandedGroupKeys: [],
    missingOnly: false,
    alertsOnly: false,
    withLessonsOnly: false,
    searchQuery: '' };
  const updateView = change => setViewState(current => ({ ...(current.context === context ? current : view), ...change }));
  const [loaded, setLoaded] = useState({ context: '', rows: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const currentContext = useRef(context);
  currentContext.current = context;
  const scrollRestored = useRef(false);
  const rows = loaded.context === context ? loaded.rows : [];
  const ready = loaded.context === context && !busy && !error;
  const canOperate = loaded.context === context && !busy;
  useEffect(() => {
    const request = ++generation.current;
    setError(''); setBusy(true);
    if (!month) { setLoaded({ context, rows: [] }); setBusy(false); return undefined; }
    service.getSettlements(month, activeSchoolYearStartYear).then(result => {
      if (generation.current !== request || currentContext.current !== context) return;
      const nextRows = result.filter(row => row.teacherActive !== false && (!personal || row.teacherId === user.teacherId));
      setLoaded({ context, rows: nextRows });
      setViewState(current => (current.context === context
        ? { ...current, expandedTeacherIds: current.expandedTeacherIds.filter(id => nextRows.some(row => row.teacherId === id)) } : current));
      if (!scrollRestored.current && restored && restored.month === month) {
        scrollRestored.current = true;
        setTimeout(() => { if (currentContext.current === context) window.scrollTo(0, Number(restored.scroll) || 0); }, 0);
      }
    }).catch(err => { if (generation.current === request && currentContext.current === context) setError(err.message || 'PAYROLL_ERROR'); })
      .finally(() => { if (generation.current === request && currentContext.current === context) setBusy(false); });
    return () => { generation.current += 1; };
  }, [context, reload, service]);
  const runOperation = async (operation, refresh = true) => {
    const request = generation.current;
    if (!canOperate) return undefined;
    setBusy(true); setError('');
    try {
      const result = await operation();
      if (request !== generation.current || currentContext.current !== context) return undefined;
      if (refresh) setReload(value => value + 1);
      else setBusy(false);
      return result || true;
    } catch (err) {
      if (request === generation.current && currentContext.current === context) { setError(err.message || 'PAYROLL_ERROR'); setBusy(false); }
      return undefined;
    }
  };
  const command = (row, input) => runOperation(() => service.saveSettlement(row, input));
  const toggleTeacher = id => updateView({ expandedTeacherIds: view.expandedTeacherIds.includes(id)
    ? view.expandedTeacherIds.filter(item => item !== id) : [...view.expandedTeacherIds, id],
  openedTeacherIds: [...new Set([...view.openedTeacherIds, id])] });
  const toggleGroup = key => updateView({ expandedGroupKeys: view.expandedGroupKeys.includes(key)
    ? view.expandedGroupKeys.filter(item => item !== key) : [...view.expandedGroupKeys, key] });
  const returnLocation = () => ({ pathname: location.pathname || (personal ? '/manage/my-settlements' : '/manage/reports/settlements'),
    search: location.search || '',
    state: { ...(location.state || {}), teacherSettlementView: settlementReturnState({ contextKey, month, ...view, scroll: window.scrollY }) } });
  const attendanceLink = lesson => settlementAttendanceLocation(lesson, returnLocation(), location.search, activeSchoolYearStartYear);
  const onAttendanceClick = (event, lesson) => {
    if (!history || !isPlainNavigationClick(event)) return;
    const target = attendanceLink(lesson);
    if (!target) return;
    event.preventDefault();
    history.replace(target.state.settlementReturn);
    history.push(target);
  };
  const total = rows.reduce((sum, row) => sum + Math.round(row.totalPayoutAmount * 100), 0) / 100;
  const missing = rows.reduce((sum, row) => sum + ((row.verification && row.verification.missingAttendanceCount) || 0), 0);
  const approvedCount = rows.filter(row => row.status === 'approved').length;
  const alertCount = rows.filter(hasPayrollAlerts).length;
  const readyCount = rows.filter(row => row.status === 'pending_approval' && row.verification && row.verification.canApprove === true && !hasPayrollAlerts(row)).length;
  const lessonCount = rows.reduce((sum, row) => sum + (row.lessonView ? row.lessonView.length : row.totalLessonsCount), 0);
  const paidCount = rows.reduce((sum, row) => sum + row.payoutEligibleCount, 0);
  const visibleIds = new Set(rows.filter(row => (!view.alertsOnly || hasPayrollAlerts(row)) &&
    (!view.withLessonsOnly || (row.totalLessonsCount > 0) || (row.lessonView || []).some(lesson => lesson.kind !== 'fixed')) &&
    settlementMatchesSearch(row, view.searchQuery, t)).map(row => row.teacherId));
  const expandAll = () => updateView({ expandedTeacherIds: [...new Set([...view.expandedTeacherIds, ...visibleIds])],
    openedTeacherIds: [...new Set([...view.openedTeacherIds, ...visibleIds])] });
  const showProblem = (row, problem) => {
    const group = groupSettlementLessons(row.lessonView || []).find(item => item.lessons.some(lesson => lesson.lessonId === problem.lessonId));
    if (!group) return;
    updateView({ expandedGroupKeys: [...new Set([...view.expandedGroupKeys, JSON.stringify([row.teacherId, group.key])])], missingOnly: false });
    setTimeout(() => {
      if (currentContext.current !== context) return;
      const target = document.getElementById(`teacher-lesson-${row.teacherId}-${problem.lessonId}`);
      if (target) { if (target.scrollIntoView) target.scrollIntoView({ block: 'center' }); target.focus({ preventScroll: true }); }
    }, 0);
  };
  return (
    <div className="teacher-payroll mb-5">
      <div className="payroll-page-heading">
        <div><h2 className="manage-title">{t(personal ? 'mine' : 'title')}</h2><p>{t('pageHint')}</p></div>
        <div className="payroll-exports">
          <div className="payroll-month"><label className="sr-only" htmlFor="payroll-month">{t('month')}</label>
            <select id="payroll-month" className="form-control" value={month || ''} disabled={busy} onChange={event => setChosenMonth(event.target.value)}>
              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {new Intl.DateTimeFormat(language, { month: 'long', year: 'numeric' }).format(new Date(`${option.value}-01T12:00:00`))}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="btn payroll-outline-button" disabled={!ready || !rows.length} onClick={() => exportPayrollCsv(rows, month, t, language)}>
            <i className="fas fa-file-csv" aria-hidden="true" /> {t('csv')}
          </button>
          <button type="button" className="btn payroll-outline-button" disabled={!ready || !rows.length} onClick={() => { if (!printPayroll(rows, month, t, language)) setError('POPUP_BLOCKED'); }}>
            <i className="fas fa-print" aria-hidden="true" /> {t('pdf')}
          </button>
          <button type="button" className="btn payroll-outline-button" disabled={busy} onClick={() => setReload(value => value + 1)}>
            <i className="fas fa-sync-alt" aria-hidden="true" /> {t('refresh')}
          </button>
        </div>
      </div>
      {error && <div className="alert alert-danger" role="alert">{error === 'POPUP_BLOCKED' ? t('popup') : errorText(error)}</div>}
      {busy && <p role="status">{t('loading')}</p>}
      {loaded.context === context && (
        <div className="payroll-summary">
          <div className="payroll-summary-payout"><span>{t('payoutTotal')}</span><strong>{formatLedgerAmount(total)}</strong>
            {missing > 0 && <small className="payroll-summary-note">{t('missingAttendance', { count: missing })}</small>}
          </div>
          <div><span>{t('paidLessons')}</span><strong>{paidCount} <small>/ {lessonCount}</small></strong></div>
          <div><span>{t('teacher')}</span><strong>{approvedCount} <small>/ {rows.length} {t('approvedShort')}</small></strong>
            <small className="payroll-summary-note">{t('readyCount', { count: readyCount })}</small>
          </div>
          <div className={alertCount ? 'payroll-summary--missing' : ''}><span>{t('needsAttention')}</span><strong>{alertCount}</strong>
            <button type="button" className="payroll-text-button" onClick={() => updateView({ alertsOnly: true, searchQuery: '' })}>{t('showWithAlerts')}</button>
          </div>
        </div>
      )}
      <div className="payroll-toolbar">
        <label className="payroll-search" htmlFor="payroll-search"><i className="fas fa-search" aria-hidden="true" /><span className="sr-only">{t('search')}</span>
          <input
            id="payroll-search"
            type="search"
            className="form-control"
            placeholder={t('search')}
            maxLength={200}
            value={view.searchQuery}
            onChange={event => updateView({ searchQuery: event.target.value })}
          />
        </label>
        <label className="payroll-filter" htmlFor="payroll-alerts-only"><input
          id="payroll-alerts-only"
          type="checkbox"
          checked={view.alertsOnly}
          onChange={event => updateView({ alertsOnly: event.target.checked })}
        /> {t('onlyAlerts')}
        </label>
        <label className="payroll-filter" htmlFor="payroll-missing-only"><input
          id="payroll-missing-only"
          type="checkbox"
          checked={view.missingOnly}
          onChange={event => updateView({ missingOnly: event.target.checked })}
        /> {t('onlyMissingAttendance')}
        </label>
        <label className="payroll-filter" htmlFor="payroll-with-lessons-only"><input
          id="payroll-with-lessons-only"
          type="checkbox"
          checked={view.withLessonsOnly}
          onChange={event => updateView({ withLessonsOnly: event.target.checked })}
        /> {t('onlyWithLessons')}
        </label>
        <div className="payroll-expand-actions">
          <button type="button" className="payroll-text-button" onClick={expandAll}>{t('expandAll')}</button>
          <button type="button" className="payroll-text-button" onClick={() => updateView({ expandedTeacherIds: [] })}>{t('collapseAll')}</button>
        </div>
      </div>
      <div className="payroll-teachers">
        <div className="payroll-list-heading" aria-hidden="true"><span>{t('teacher')}</span><span>{t('lessons')}</span><span>{t('amount')}</span><span>{t('status')}</span></div>
        {rows.map(row => {
          const expanded = view.expandedTeacherIds.includes(row.teacherId);
          const missingCount = row.verification && row.verification.missingAttendanceCount;
          const blocked = hasPayrollAlerts(row);
          let tone = 'warning';
          let {status} = row;
          if (row.status === 'approved') tone = 'success';
          else if (missingCount > 0) { tone = 'danger'; status = 'needsCompletion'; } else if (blocked) status = 'needsReview';
          else if (row.status === 'pending_approval' && row.verification.canApprove === true) { tone = 'info'; status = 'readyForApproval'; }
          const canApprove = ready && row.verification && row.verification[personal ? 'canConfirm' : 'canApprove'] === true &&
            (personal ? row.teacherId === user.teacherId : canReadPayments(user));
          const initials = String(row.teacherName || '').split(/\s+/).filter(Boolean).slice(0, 2)
            .map(part => Array.from(part)[0])
            .join('');
          return (
            <article
              hidden={!visibleIds.has(row.teacherId)}
              className={`payroll-teacher${expanded ? ' payroll-teacher--expanded' : ''}`}
              key={row.teacherId}
              id={`settlement-teacher-${row.teacherId}`}
            >
              <button
                type="button"
                className="payroll-teacher-toggle"
                aria-label={row.teacherName}
                aria-describedby={`teacher-meta-${row.teacherId} teacher-lessons-${row.teacherId} teacher-amount-${row.teacherId} teacher-status-${row.teacherId}`}
                aria-expanded={expanded}
                aria-controls={`teacher-details-${row.teacherId}`}
                onClick={() => toggleTeacher(row.teacherId)}
              >
                <span className="payroll-teacher-identity"><span className="payroll-avatar" aria-hidden="true">{initials}</span>
                  <span>
                    <strong>{row.teacherName}</strong>
                    <small id={`teacher-meta-${row.teacherId}`} className={missingCount > 0 ? 'payroll-danger-text' : ''}>
                      {missingCount > 0 ? t('missingAttendance', { count: missingCount }) : t('groupCount', { count: groupSettlementLessons(row.lessonView || []).length })}
                    </small>
                  </span>
                </span>
                <span id={`teacher-lessons-${row.teacherId}`} className="payroll-teacher-lessons">
                  {row.payoutEligibleCount} <span>/ {row.lessonView ? row.lessonView.length : row.totalLessonsCount} {t('meetings')}</span>
                </span>
                <strong id={`teacher-amount-${row.teacherId}`} className="payroll-teacher-amount">{formatLedgerAmount(row.totalPayoutAmount)}</strong>
                <TeacherSettlementStatus id={`teacher-status-${row.teacherId}`} tone={tone}>{t(status)}</TeacherSettlementStatus>
                <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} payroll-chevron`} aria-hidden="true" />
              </button>
              {view.openedTeacherIds.includes(row.teacherId) && (
                <div id={`teacher-details-${row.teacherId}`} hidden={!expanded} className="payroll-teacher-details" role="region" aria-label={`${t('details')} · ${row.teacherName}`}>
                  {blocked && (
                  <div className="payroll-problems" role="note">
                    {!row.verification && <p>{t('verificationUnavailable')}</p>}
                    {(row.warnings || []).length > 0 && <p>{t('warnings')}: {row.warnings.map(errorText).join(' ')}</p>}
                    {((row.verification && row.verification.problems) || []).map((problem, index) => {
                      const knownLesson = (row.lessonView || []).some(lesson => lesson.lessonId === problem.lessonId);
                      return (
                        <div className="payroll-alert-row" key={`${problem.code || problem}:${problem.lessonId || index}`}>
                          <i className="fas fa-exclamation-circle" aria-hidden="true" />
                          <span>{errorText(problem.code || problem)}{problem.date && ` · ${displayDate(problem.date, language)}`}</span>
                          {knownLesson && (
                          <button type="button" className="payroll-text-button" onClick={() => showProblem(row, problem)}>
                            {t('showProblem')} <i className="fas fa-arrow-right" aria-hidden="true" />
                          </button>
                          )}
                        </div>
                      );
                    })}
                    {row.verification && row.verification.requiresRecalculation && <p>{t('recalculationRequired')}</p>}
                    {row.issue && <p>{t('issue')}: {row.issue}</p>}
                  </div>
                  )}
                  <TeacherSettlementDetails
                    key={`${context}:${row.teacherId}`}
                    report={row}
                    ready={canOperate && (personal || canReadPayments(user))}
                    personal={personal}
                    service={service}
                    runOperation={runOperation}
                    command={command}
                    expandedGroupKeys={view.expandedGroupKeys}
                    missingOnly={view.missingOnly}
                    onToggleGroup={toggleGroup}
                    attendanceLink={attendanceLink}
                    onAttendanceClick={onAttendanceClick}
                    t={t}
                    language={language}
                  />
                  <div className="payroll-teacher-footer">
                    <span>{t('payoutTotal')}: <strong>{formatLedgerAmount(row.totalPayoutAmount)}</strong></span>
                    {row.status !== 'approved' && (
                    <div>
                      {blocked && <span className="payroll-danger-text">{t('completeToApprove')}</span>}
                      <button
                        type="button"
                        className="btn manage-red-button"
                        disabled={!canApprove}
                        onClick={() => command(row, { status: personal ? 'pending_approval' : 'approved' })}
                      >{t(personal ? 'confirm' : 'approve')}
                      </button>
                    </div>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
      {ready && rows.length > 0 && !visibleIds.size && <p role="status" className="payroll-no-results">{t('noResults')}</p>}
      {ready && !rows.length && <p>{t('empty')}</p>}
    </div>
  );
}
TeacherSettlementsReport.propTypes = { activeSchoolYearStartYear: PropTypes.number.isRequired,
  user: PropTypes.object.isRequired,
  personal: PropTypes.bool,
  service: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object };
TeacherSettlementsReport.defaultProps = { personal: false, service: TeacherLedgerService, history: null, location: {} };
export default withRouter(connect(state => ({ activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear, user: state.user }))(TeacherSettlementsReport));
